const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/**
 * Formats a byte count into a compact display label.
 *
 * Drops the decimal once the value reaches double digits ("46 MB", not
 * "45.9 MB") — the extra precision stops being useful at that scale.
 *
 * @param bytes - Byte count. Accepts the string form the files API returns.
 * @returns Formatted size, or null when the input is not a usable number
 *
 * @example
 * formatBytes(1536)       // '1.5 KB'
 * formatBytes('48160768') // '46 MB'
 */
export function formatBytes(
  bytes: number | string | undefined | null,
): string | null {
  const value = typeof bytes === 'string' ? Number(bytes) : bytes;

  if (value === undefined || value === null) return null;
  if (!Number.isFinite(value) || value < 0) return null;
  if (value === 0) return '0 B';

  const unitIndex = Math.min(
    Math.floor(Math.log(value) / Math.log(1024)),
    BYTE_UNITS.length - 1,
  );
  const scaled = value / 1024 ** unitIndex;

  return `${scaled.toFixed(scaled >= 10 || unitIndex === 0 ? 0 : 1)} ${BYTE_UNITS[unitIndex]}`;
}

/**
 * Parses the `size_bytes` field returned by the Bishop files API.
 *
 * The generated model types it as an optional string because the underlying
 * protobuf field is an int64. Anything unparseable is treated as "unknown"
 * rather than zero, so callers can distinguish "no metadata yet" from "empty
 * file" and avoid gating on a size they do not actually have.
 *
 * @param sizeBytes - Raw `size_bytes` value from `V1FileMetadata`
 * @returns The size as a number, or null when it is missing or invalid
 */
export function parseSizeBytes(
  sizeBytes: string | undefined | null,
): number | null {
  if (sizeBytes === undefined || sizeBytes === null || sizeBytes === '') {
    return null;
  }

  const parsed = Number(sizeBytes);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}
